import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { auth, db } from "../../firebase";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  where,
  getDoc,
} from "firebase/firestore";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
} from "@mui/material";

import { Edit, Delete } from "@mui/icons-material";

export default function AddExpense() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    timer: 3000,
    showConfirmButton: false,
  });

  const [user, setUser] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [expensehead, setExpenseHead] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const [headid, setHeadid] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  /* ================= AUTH + PROPERTY ================= */

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUser(u);
        const snap = await getDoc(doc(db, "authroles", u.uid));
        if (snap.exists()) {
          setPropertyId(snap.data().propertyId);
        }
      }
    });
    return () => unsub();
  }, []);

  /* ================= FETCH HEAD ================= */

  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "expensehead"),
      where("propertyId", "==", propertyId),
    );

    const unsub = onSnapshot(q, (snap) => {
      setExpenseHead(
        snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= FETCH EXPENSES ================= */

  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "expenses"),
      where("propertyId", "==", propertyId),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      setExpenses(
        snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= ADD ================= */

  const handleSubmit = async () => {
    if (!headid || !description || !amount) {
      Toast.fire({ icon: "error", title: "Fill required fields" });
      return;
    }

    await addDoc(collection(db, "expenses"), {
      headid,
      description,
      amount,
      note,
      date,
      propertyId,
      uid: user.uid,
      createdAt: Timestamp.now(),
    });

    setHeadid("");
    setDescription("");
    setAmount("");
    setNote("");
    setDate("");

    Toast.fire({ icon: "success", title: "Expense Added" });
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    await deleteDoc(doc(db, "expenses", id));
    Toast.fire({ icon: "success", title: "Deleted" });
  };

  /* ================= UPDATE ================= */

 const handleUpdate = async () => {
  if (!editData.headid || !editData.description || !editData.amount) {
    Toast.fire({ icon: "error", title: "Fill required fields" });
    return;
  }

  await updateDoc(doc(db, "expenses", editData.id), {
    headid: editData.headid,
    description: editData.description,
    amount: editData.amount,
    note: editData.note || "",
    date: editData.date || "",
  });

  setEditOpen(false);
  Toast.fire({ icon: "success", title: "Updated Successfully" });
};

  /* ================= SEARCH ================= */

  const filtered = expenses.filter(
    (item) =>
      item.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.amount?.toString().includes(searchText)
  );

  /* ================= UI ================= */

  return (
    <Box className="content-wrapper" p={3}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Expense
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth size="small"
                label="Expense Head"
                value={headid}
                onChange={(e) => setHeadid(e.target.value)}
              >
                {expensehead.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.headname}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth size="small"
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth size="small"
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth size="small"
                sx={{ height: "100%" }}
                onClick={handleSubmit}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Expense List */}
      <Card>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">Expense List</Typography>

            <TextField
              size="small"
              label="Search..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
            />
          </Stack>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Head</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => {
                    const head = expensehead.find(
                      (h) => h.id === item.headid
                    );

                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>
                          {moment(item.date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>{head?.headname}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.amount}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setEditData(item);
                              setEditOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* EDIT DIALOG */}
     {/* EDIT DIALOG */}
<Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
  <DialogTitle>Edit Expense</DialogTitle>

  <DialogContent>

    {/* Expense Head */}
    <TextField
      select
      fullWidth
      size="small"
      margin="dense"
      label="Expense Head"
      value={editData.headid || ""}
      onChange={(e) =>
        setEditData({ ...editData, headid: e.target.value })
      }
    >
      {expensehead.map((item) => (
        <MenuItem key={item.id} value={item.id}>
          {item.headname}
        </MenuItem>
      ))}
    </TextField>

    {/* Description */}
    <TextField
      fullWidth
      size="small"
      margin="dense"
      label="Description"
      value={editData.description || ""}
      onChange={(e) =>
        setEditData({ ...editData, description: e.target.value })
      }
    />

    {/* Amount */}
    <TextField
      fullWidth
      size="small"
      margin="dense"
      label="Amount"
      value={editData.amount || ""}
      onChange={(e) =>
        setEditData({ ...editData, amount: e.target.value })
      }
    />

    {/* Date */}
    <TextField
      type="date"
      fullWidth
      size="small"
      margin="dense"
      InputLabelProps={{ shrink: true }}
      value={editData.date || ""}
      onChange={(e) =>
        setEditData({ ...editData, date: e.target.value })
      }
    />

   

  </DialogContent>

  <DialogActions>
    <Button onClick={() => setEditOpen(false)}>Cancel</Button>
    <Button variant="contained" onClick={handleUpdate}>
      Update
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
}